import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAdd, MdClose, MdContentCopy, MdDelete, MdVpnKey } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    createVideoApiKey,
    listVideoApiKeys,
    revokeVideoApiKey,
    type VideoApiKey,
} from '@/lib/api/video/video-api'

function formatDateTime(value: string | null): string {
    if (!value) return 'Nunca'
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

export default function VideoApiKeysPage() {
    const { canCreate, canDelete } = usePermission()
    const [keys, setKeys] = useState<VideoApiKey[]>([])
    const [loading, setLoading] = useState(true)
    const [showCreate, setShowCreate] = useState(false)
    const [confirmRevoke, setConfirmRevoke] = useState<VideoApiKey | null>(null)
    const [revoking, setRevoking] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        const response = await listVideoApiKeys()
        setKeys(response.success ? (response.result ?? []) : [])
        setLoading(false)
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const handleRevoke = async () => {
        if (!confirmRevoke) return
        setRevoking(true)
        const response = await revokeVideoApiKey(confirmRevoke.id)
        setRevoking(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível revogar a chave')
            return
        }
        toast.success('Chave revogada')
        setConfirmRevoke(null)
        void load()
    }

    return (
        <div className="p-4 sm:p-6 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold">Chaves de API</h1>
                    <p className="text-sm text-base-content/60 mt-0.5">
                        Usadas pelo seu sistema para criar salas e gerar links de entrada
                    </p>
                </div>
                {canCreate('video', 'api') && (
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
                        <MdAdd className="text-lg" />
                        Nova chave
                    </button>
                )}
            </div>

            <div className="alert py-2">
                <span className="text-sm">
                    A chave dá acesso total à API de vídeo da organização. Guarde-a só no servidor do seu
                    sistema, nunca no navegador ou no aplicativo.
                </span>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : !keys.length ? (
                <div className="text-center py-16">
                    <MdVpnKey className="text-6xl mx-auto text-base-content/20 mb-3" />
                    <p className="font-medium">Nenhuma chave criada</p>
                </div>
            ) : (
                <>
                <div className="space-y-3 sm:hidden">
                    {keys.map(key => (
                        <div key={key.id} className="rounded-xl border border-base-200 bg-base-100 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{key.name}</div>
                                    <div className="font-mono text-xs text-base-content/50 break-all">mmk_{key.keyId}</div>
                                </div>
                                <span
                                    className={`badge badge-sm shrink-0 ${key.revokedAt ? 'badge-neutral' : 'badge-success'}`}
                                >
                                    {key.revokedAt ? 'Revogada' : 'Ativa'}
                                </span>
                            </div>
                            <div className="mt-2 text-xs text-base-content/60">
                                Último uso: {formatDateTime(key.lastUsedAt)} · Criada em {formatDateTime(key.createdAt)}
                            </div>
                            {!key.revokedAt && canDelete('video', 'api') && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline btn-error w-full mt-3"
                                    onClick={() => setConfirmRevoke(key)}
                                >
                                    <MdDelete />
                                    Revogar
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="overflow-x-auto hidden sm:block">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th className="w-48">Identificador</th>
                                <th className="w-32">Situação</th>
                                <th className="w-40">Último uso</th>
                                <th className="w-40">Criada em</th>
                                <th className="w-20 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map(key => (
                                <tr key={key.id} className="hover">
                                    <td className="font-medium">{key.name}</td>
                                    <td className="font-mono text-xs text-base-content/60">mmk_{key.keyId}</td>
                                    <td>
                                        <span
                                            className={`badge badge-sm ${key.revokedAt ? 'badge-neutral' : 'badge-success'}`}
                                        >
                                            {key.revokedAt ? 'Revogada' : 'Ativa'}
                                        </span>
                                    </td>
                                    <td className="text-base-content/60">{formatDateTime(key.lastUsedAt)}</td>
                                    <td className="text-base-content/60">{formatDateTime(key.createdAt)}</td>
                                    <td className="text-right">
                                        {!key.revokedAt && canDelete('video', 'api') && (
                                            <button
                                                type="button"
                                                className="btn btn-xs btn-ghost text-error"
                                                onClick={() => setConfirmRevoke(key)}
                                                title="Revogar"
                                            >
                                                <MdDelete />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            {showCreate && (
                <CreateKeyModal
                    onClose={() => {
                        setShowCreate(false)
                        void load()
                    }}
                />
            )}

            <ConfirmModal
                isOpen={Boolean(confirmRevoke)}
                onClose={() => setConfirmRevoke(null)}
                onConfirm={handleRevoke}
                loading={revoking}
                title="Revogar chave"
                message="O sistema que usa esta chave deixa de conseguir criar salas e gerar links na hora. Salas em andamento continuam. Esta ação não pode ser desfeita."
                confirmButtonText="Revogar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}

function CreateKeyModal({ onClose }: { onClose: () => void }) {
    const [name, setName] = useState('')
    const [secret, setSecret] = useState<string | null>(null)
    const [creating, setCreating] = useState(false)

    const handleCreate = async () => {
        if (!name.trim()) {
            toast.error('Dê um nome à chave')
            return
        }
        setCreating(true)
        const response = await createVideoApiKey(name.trim())
        setCreating(false)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível criar a chave')
            return
        }
        setSecret(response.result.secret)
    }

    const handleCopy = async () => {
        if (!secret) return
        await navigator.clipboard.writeText(secret)
        toast.success('Chave copiada')
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MdVpnKey className="text-xl text-primary" />
                        Nova chave de API
                    </h3>
                    <button type="button" className="btn btn-sm btn-ghost btn-circle" onClick={onClose}>
                        <MdClose className="text-xl" />
                    </button>
                </div>

                {secret ? (
                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-key-secret">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Chave</span>
                                </span>
                            </label>
                            <div className="join w-full">
                                <input
                                    id="video-key-secret"
                                    type="text"
                                    className="input input-bordered join-item w-full font-mono text-xs"
                                    value={secret}
                                    readOnly
                                />
                                <button type="button" className="btn btn-primary join-item" onClick={handleCopy}>
                                    <MdContentCopy />
                                    Copiar
                                </button>
                            </div>
                        </div>
                        <div className="alert alert-warning py-2">
                            <span className="text-sm">
                                Copie agora: a chave não será mostrada de novo. Se perder, revogue e crie outra.
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="form-control w-full">
                        <label className="label pb-1.5" htmlFor="video-key-name">
                            <span className="flex w-full items-baseline justify-between gap-3">
                                <span className="label-text font-medium">Nome</span>
                            </span>
                        </label>
                        <input
                            id="video-key-name"
                            type="text"
                            className="input input-bordered w-full"
                            value={name}
                            maxLength={80}
                            placeholder="Ex.: Servidor de produção"
                            onChange={event => setName(event.target.value)}
                        />
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        {secret ? 'Concluir' : 'Cancelar'}
                    </button>
                    {!secret && (
                        <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                            {creating && <span className="loading loading-spinner loading-sm" />}
                            Criar chave
                        </button>
                    )}
                </div>
            </div>
            <div className="modal-backdrop" />
        </div>
    )
}
